<template>
  <div class="analytics-dashboard-overview">
    <div class="section-header">
      <h3>Analytics Dashboard</h3>
      <p>
        High-level insights and performance metrics across all scouting
        activities
      </p>
    </div>

    <!-- Key Performance Indicators -->
    <div class="kpi-cards">
      <div class="kpi-card">
        <div class="kpi-icon primary">
          <i class="bi bi-graph-up-arrow"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ totalAssessments }}</h4>
          <p>Total Player Assessments</p>
          <span class="kpi-trend trend-positive" v-if="assessmentGrowth > 0">
            +{{ assessmentGrowth }}% this season
          </span>
          <span
            class="kpi-trend trend-negative"
            v-else-if="assessmentGrowth < 0"
          >
            {{ assessmentGrowth }}% this season
          </span>
          <span class="kpi-trend trend-neutral" v-else>
            No change this season
          </span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-icon success">
          <i class="bi bi-trophy"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ signedPlayers }}</h4>
          <p>Players Signed</p>
          <span class="kpi-detail">
            {{ conversionRateOverall }}% conversion rate
          </span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-icon warning">
          <i class="bi bi-geo-alt"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ activeScouts }}</h4>
          <p>Active Scouts</p>
          <span class="kpi-detail"> Covering {{ regionsCount }} regions </span>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-icon info">
          <i class="bi bi-currency-pound"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ rejectedPlayers }}</h4>
          <p>Players rejected</p>
          <span class="kpi-trend trend-neutral">
            {{ conversionRateOverallRejected }}% rejection rate
          </span>
        </div>
      </div>
    </div>

    <!-- Dashboard Sections -->
    <div class="dashboard-sections">
      <!-- Performance Benchmarking Section -->
      <div class="dashboard-section" @click="navigateToPerformanceBenchmarking">
        <div class="section-header-card">
          <div class="section-icon performance">
            <i class="bi bi-bullseye"></i>
          </div>
          <div class="section-info">
            <h4>Performance Benchmarking</h4>
            <p>Rating distributions, scout accuracy, and success predictions</p>
            <div class="section-metrics">
              <span class="metric">{{ overallAccuracy }}% accuracy</span>
              <span class="metric">{{ benchmarkSampleSize }} assessments</span>
            </div>
          </div>
          <div class="section-action">
            <i class="bi bi-arrow-right"></i>
          </div>
        </div>

        <!-- Quick Preview Charts -->
        <div class="preview-charts">
          <div class="mini-chart">
            <h5>Rating Distribution</h5>
            <canvas
              ref="miniDistributionChart"
              width="300"
              height="120"
            ></canvas>
          </div>
          <div class="mini-stats">
            <div class="stat-item">
              <span class="stat-label">Best Category</span>
              <span class="stat-value">{{ bestCategory }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Avg Rating</span>
              <span class="stat-value">{{ averageRating }}/10</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Prediction Trend</span>
              <span class="stat-value trend-positive"
                >+{{ accuracyTrend }}%</span
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Player Demographics Section -->
      <div class="dashboard-section" @click="navigateToPlayerDemographics">
        <div class="section-header-card">
          <div class="section-icon demographics">
            <i class="bi bi-people"></i>
          </div>
          <div class="section-info">
            <h4>Player Demographics Analysis</h4>
            <p>
              Age distribution, geographic sources, position analysis, and
              conversion funnels
            </p>
            <div class="section-metrics">
              <span class="metric">{{ activeRegionsCount }} regions</span>
              <span class="metric">{{ conversionRate }}% conversion</span>
            </div>
          </div>
          <div class="section-action">
            <i class="bi bi-arrow-right"></i>
          </div>
        </div>

        <!-- Quick Preview -->
        <div class="preview-demographics">
          <div class="demo-chart-mini">
            <h5>Geographic Distribution</h5>
            <div class="region-preview">
              <div
                v-for="region in topRegions"
                :key="region.name"
                class="region-mini"
              >
                <span class="region-name">{{ region.name }}</span>
                <div class="region-bar-mini">
                  <div
                    class="region-fill-mini"
                    :style="{ width: region.percentage + '%' }"
                  ></div>
                </div>
                <span class="region-count">{{ region.count }}</span>
              </div>
            </div>
          </div>
          <div class="demo-stats">
            <div class="stat-item">
              <span class="stat-label">Avg Age</span>
              <span class="stat-value">{{ averagePlayerAge }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Top Position</span>
              <span class="stat-value">{{ topPosition }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Peak Source</span>
              <span class="stat-value">{{ topRegion }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Scout Activity Reports Section -->
      <div class="dashboard-section" @click="navigateToScoutActivity">
        <div class="section-header-card">
          <div class="section-icon scout-activity">
            <i class="bi bi-binoculars"></i>
          </div>
          <div class="section-info">
            <h4>Scout Activity Reports</h4>
            <p>
              Games attended per scout, specialization effectiveness, and
              regional coverage maps
            </p>
            <div class="section-metrics">
              <span class="metric"
                >{{ totalGamesAttended }} games attended</span
              >
              <span class="metric">{{ scoutEfficiencyRate }}% efficiency</span>
            </div>
          </div>
          <div class="section-action">
            <i class="bi bi-arrow-right"></i>
          </div>
        </div>

        <!-- Quick Preview -->
        <div class="preview-scout-activity">
          <div class="scout-chart-mini">
            <h5>Scout Performance</h5>
            <div class="scout-preview">
              <div
                v-for="scout in topScouts"
                :key="scout.name"
                class="scout-mini"
              >
                <span class="scout-name">{{ scout.name }}</span>
                <div class="scout-bar-mini">
                  <div
                    class="scout-fill-mini"
                    :style="{ width: scout.percentage + '%' }"
                  ></div>
                </div>
                <span class="scout-count">{{ scout.gamesAttended }}</span>
              </div>
            </div>
          </div>
          <div class="scout-stats">
            <div class="stat-item">
              <span class="stat-label">Active Scouts</span>
              <span class="stat-value">{{ activeScoutsCount }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Coverage</span>
              <span class="stat-value">{{ scoutCoverageRegions }} regions</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import { Trialists } from "@/services/trialist-service.js";

export default {
  name: "AnalyticsDashboardOverview",
  props: {
    analyticsData: {
      type: Object,
      default: () => ({}),
    },
    userData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    trialistsData: {
      type: Array,
      default: () => [],
    },
    signedTrialistsData: {
      type: Array,
      default: () => [],
    },
    archivedTrialistsData: {
      type: Array,
      default: () => [],
    },
    allScoutsData: {
      type: Array,
      default: () => [],
    },
    allAssessmentsData: {
      type: Array,
      default: () => [],
    },
    gameAttendanceData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["navigate"],
  data() {
    return {
      miniDistributionChart: null,
      loadingKPIs: false, // No longer needed since we get data from props

      // Real KPI Data - will be calculated from props
      totalAssessments: 0,
      assessmentGrowth: 0,
      signedPlayers: 0,
      rejectedPlayers: 0,
      conversionRateOverall: 0,
      conversionRateOverallRejected: 0,
      activeScouts: 0,
      regionsCount: 0,

      // Performance Benchmarking Preview Data - calculated from real data
      overallAccuracy: 0,
      benchmarkSampleSize: 0,
      bestCategory: "",
      averageRating: 0,
      accuracyTrend: 0,

      // Demographics Preview Data - calculated from real data
      activeRegionsCount: 0,
      conversionRate: 0,
      averagePlayerAge: 0,
      topPosition: "",
      topRegion: "",

      topRegions: [],

      // Scout Activity Preview Data - calculated from real data
      totalGamesAttended: 0,
      scoutEfficiencyRate: 0,
      activeScoutsCount: 0,
      scoutCoverageRegions: 0,
      topScouts: [],
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.createMiniDistributionChart();
      this.calculateKPIs();
      // Try to enrich with the backend aggregation. If it succeeds, the
      // KPIs and mini chart get pre-aggregated values; if it fails, the
      // client-side calculation we just ran stays in place as a fallback.
      this.fetchAnalyticsSummary();
    });
  },
  watch: {
    // Recalculate KPIs when props change
    trialistsData() {
      this.calculateKPIs();
    },
    signedTrialistsData() {
      this.calculateKPIs();
    },
    archivedTrialistsData() {
      this.calculateKPIs();
    },
    allScoutsData() {
      this.calculateKPIs();
    },
    allAssessmentsData() {
      this.calculateKPIs();
      this.refreshMiniDistributionChart();
    },
    gameAttendanceData() {
      this.calculateKPIs();
    },
  },
  beforeUnmount() {
    // Halt any in-flight animation before destroy, otherwise the animator's
    // next tick fires after the canvas DOM node has been removed and crashes
    // inside Chart.js's clearCanvas with "Cannot read properties of null".
    if (this.miniDistributionChart) {
      try { this.miniDistributionChart.stop(); } catch (e) { /* noop */ }
      this.miniDistributionChart.destroy();
      this.miniDistributionChart = null;
    }
  },
  methods: {
    // Calculate all KPI values from props data
    calculateKPIs() {
      // 1. Total Assessments
      this.totalAssessments = this.allAssessmentsData.length;

      // 2. Signed Players - use the signedTrialistsData prop directly
      this.signedPlayers = this.signedTrialistsData.length;

      this.rejectedPlayers = this.archivedTrialistsData.length;

      // 3. Conversion Rate Overall
      this.conversionRateOverallRejected =
        this.totalAssessments > 0
          ? ((this.rejectedPlayers / this.totalAssessments) * 100).toFixed(1)
          : 0;

      // 3. Conversion Rate Overall
      this.conversionRateOverall =
        this.totalAssessments > 0
          ? ((this.signedPlayers / this.totalAssessments) * 100).toFixed(1)
          : 0;

      // 4. Active Scouts
      this.activeScouts = this.allScoutsData.length;

      // 5. Regions Count (unique regions from scout addresses/post_codes)
      this.regionsCount = this.calculateUniqueRegions();

      // 6. Assessment Growth (current season vs previous season)
      this.assessmentGrowth = this.calculateAssessmentGrowth();

      // 7. Performance Benchmarking Metrics
      this.calculatePerformanceBenchmarkingMetrics();

      // 9. Demographics Metrics
      this.calculateDemographicsMetrics();

      // 10. Scout Activity Metrics
      this.calculateScoutActivityMetrics();
    },

    // Calculate unique regions from scout locations
    calculateUniqueRegions() {
      if (!this.allScoutsData.length) return 0;

      const regions = new Set();
      this.allScoutsData.forEach((scout) => {
        if (scout.post_code) {
          // Extract region from postcode (first part before space)
          const region = scout.post_code.split(" ")[0];
          if (region) regions.add(region);
        } else if (scout.address) {
          // Use address if postcode not available
          regions.add(scout.address.split(",").pop().trim());
        }
      });
      return regions.size || 1; // At least 1 region
    },

    // Calculate assessment growth between current and previous season
    calculateAssessmentGrowth() {
      if (!this.gameAttendanceData.length) return 0;

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // JS months are 0-indexed

      // Current season: Aug 1 current year - June 30 next year
      // OR Aug 1 previous year - June 30 current year (if before Aug)
      let currentSeasonStart,
        currentSeasonEnd,
        previousSeasonStart,
        previousSeasonEnd;

      if (currentMonth >= 8) {
        // Aug - Dec: current season started this year
        currentSeasonStart = new Date(currentYear, 7, 1); // Aug 1 current year
        currentSeasonEnd = new Date(currentYear + 1, 5, 30); // June 30 next year
        previousSeasonStart = new Date(currentYear - 1, 7, 1); // Aug 1 previous year
        previousSeasonEnd = new Date(currentYear, 5, 30); // June 30 current year
      } else {
        // Jan - July: current season started last year
        currentSeasonStart = new Date(currentYear - 1, 7, 1); // Aug 1 last year
        currentSeasonEnd = new Date(currentYear, 5, 30); // June 30 current year
        previousSeasonStart = new Date(currentYear - 2, 7, 1); // Aug 1 two years ago
        previousSeasonEnd = new Date(currentYear - 1, 5, 30); // June 30 last year
      }

      // Count assessments in each season
      let currentSeasonCount = 0;
      let previousSeasonCount = 0;

      this.gameAttendanceData.forEach((game) => {
        const gameDate = new Date(game.matchDate);

        if (gameDate >= currentSeasonStart && gameDate <= currentSeasonEnd) {
          // Count assessments for this game
          const assessmentsForGame = this.allAssessmentsData.filter(
            (assessment) => assessment.scoutAttendanceReportId === game.id,
          ).length;
          currentSeasonCount += assessmentsForGame;
        }

        if (gameDate >= previousSeasonStart && gameDate <= previousSeasonEnd) {
          const assessmentsForGame = this.allAssessmentsData.filter(
            (assessment) => assessment.scoutAttendanceReportId === game.id,
          ).length;
          previousSeasonCount += assessmentsForGame;
        }
      });

      // Calculate percentage growth
      if (previousSeasonCount === 0) return currentSeasonCount > 0 ? 100 : 0;

      return (
        ((currentSeasonCount - previousSeasonCount) / previousSeasonCount) *
        100
      ).toFixed(1);
    },

    // Calculate Performance Benchmarking Metrics from real assessment data
    calculatePerformanceBenchmarkingMetrics() {
      if (!this.allAssessmentsData.length) {
        this.overallAccuracy = 0;
        this.benchmarkSampleSize = 0;
        this.bestCategory = "N/A";
        this.averageRating = 0;
        this.accuracyTrend = 0;
        return;
      }

      // Benchmark sample size is total assessments
      this.benchmarkSampleSize = this.allAssessmentsData.length;

      // Calculate average ratings across all assessment categories
      const ratingCategories = [
        "attitude",
        "skill",
        "physique",
        "mentality",
        "leadership",
        "professionalism",
      ];
      const categoryAverages = {};
      let totalRating = 0;
      let totalRatings = 0;

      ratingCategories.forEach((category) => {
        let categorySum = 0;
        let categoryCount = 0;

        this.allAssessmentsData.forEach((assessment) => {
          if (assessment[category] && !isNaN(assessment[category])) {
            categorySum += parseFloat(assessment[category]);
            categoryCount++;
          }
        });

        if (categoryCount > 0) {
          categoryAverages[category] = (categorySum / categoryCount).toFixed(1);
          totalRating += categorySum;
          totalRatings += categoryCount;
        }
      });

      // Overall average rating
      this.averageRating =
        totalRatings > 0 ? (totalRating / totalRatings).toFixed(1) : 0;

      // Find best category (highest average)
      let bestCategoryName = "N/A";
      let bestCategoryScore = 0;
      Object.keys(categoryAverages).forEach((category) => {
        if (parseFloat(categoryAverages[category]) > bestCategoryScore) {
          bestCategoryScore = parseFloat(categoryAverages[category]);
          bestCategoryName =
            category.charAt(0).toUpperCase() + category.slice(1);
        }
      });
      this.bestCategory = bestCategoryName;

      // Calculate overall accuracy based on successful outcome predictions
      // This is a simplified calculation - in reality, you'd compare predictions to actual outcomes
      const successfulOutcomes = this.signedTrialistsData.length;
      const totalOutcomes = this.totalAssessments;
      this.overallAccuracy =
        totalOutcomes > 0
          ? ((successfulOutcomes / totalOutcomes) * 100).toFixed(1)
          : 0;

      // Calculate accuracy trend (simplified - compare current vs previous season accuracy)
      this.accuracyTrend = this.calculateAccuracyTrend();
    },

    // Calculate accuracy trend over time (simplified version)
    calculateAccuracyTrend() {
      if (!this.gameAttendanceData.length || !this.allAssessmentsData.length)
        return 0;

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;

      // Define season boundaries (same as assessment growth calculation)
      let currentSeasonStart,
        currentSeasonEnd,
        previousSeasonStart,
        previousSeasonEnd;

      if (currentMonth >= 8) {
        currentSeasonStart = new Date(currentYear, 7, 1);
        currentSeasonEnd = new Date(currentYear + 1, 5, 30);
        previousSeasonStart = new Date(currentYear - 1, 7, 1);
        previousSeasonEnd = new Date(currentYear, 5, 30);
      } else {
        currentSeasonStart = new Date(currentYear - 1, 7, 1);
        currentSeasonEnd = new Date(currentYear, 5, 30);
        previousSeasonStart = new Date(currentYear - 2, 7, 1);
        previousSeasonEnd = new Date(currentYear - 1, 5, 30);
      }

      // Get assessments for each season
      let currentSeasonAssessments = 0;
      let currentSeasonSuccesses = 0;
      let previousSeasonAssessments = 0;
      let previousSeasonSuccesses = 0;

      this.gameAttendanceData.forEach((game) => {
        const gameDate = new Date(game.matchDate);
        const gameAssessments = this.allAssessmentsData.filter(
          (assessment) => assessment.scoutAttendanceReportId === game.id,
        );

        if (gameDate >= currentSeasonStart && gameDate <= currentSeasonEnd) {
          currentSeasonAssessments += gameAssessments.length;
          // Count "successes" as assessments with above-average ratings
          currentSeasonSuccesses += gameAssessments.filter((assessment) => {
            const avgRating = this.calculateAssessmentAverageRating(assessment);
            return avgRating >= 6; // Consider 6+ as successful prediction
          }).length;
        }

        if (gameDate >= previousSeasonStart && gameDate <= previousSeasonEnd) {
          previousSeasonAssessments += gameAssessments.length;
          previousSeasonSuccesses += gameAssessments.filter((assessment) => {
            const avgRating = this.calculateAssessmentAverageRating(assessment);
            return avgRating >= 6;
          }).length;
        }
      });

      // Calculate accuracy for each season
      const currentAccuracy =
        currentSeasonAssessments > 0
          ? (currentSeasonSuccesses / currentSeasonAssessments) * 100
          : 0;
      const previousAccuracy =
        previousSeasonAssessments > 0
          ? (previousSeasonSuccesses / previousSeasonAssessments) * 100
          : 0;

      // Return trend
      if (previousAccuracy === 0) return currentAccuracy > 0 ? 100 : 0;
      return (
        ((currentAccuracy - previousAccuracy) / previousAccuracy) *
        100
      ).toFixed(1);
    },

    // Helper method to calculate average rating for a single assessment
    calculateAssessmentAverageRating(assessment) {
      const ratingCategories = [
        "attitude",
        "skill",
        "physique",
        "mentality",
        "leadership",
        "professionalism",
      ];
      let total = 0;
      let count = 0;

      ratingCategories.forEach((category) => {
        if (assessment[category] && !isNaN(assessment[category])) {
          total += parseFloat(assessment[category]);
          count++;
        }
      });

      return count > 0 ? total / count : 0;
    },

    // Calculate Demographics Metrics from real trialist and assessment data
    calculateDemographicsMetrics() {
      // Use the conversion rate already calculated for the main dashboard
      this.conversionRate = parseFloat(this.conversionRateOverall) || 0;

      // Active regions count (use the same calculation as regionsCount)
      this.activeRegionsCount = this.regionsCount;

      // Calculate demographics from trialists data
      this.calculateTrialistDemographics();

      // Calculate regional data from assessments and scouts
      this.calculateRegionalData();
    },

    // Calculate trialist demographics (age, position)
    calculateTrialistDemographics() {
      const allTrialists = [
        ...this.trialistsData,
        ...this.signedTrialistsData,
        ...this.archivedTrialistsData,
      ];

      if (!allTrialists.length) {
        this.averagePlayerAge = 0;
        this.topPosition = "N/A";
        return;
      }

      // Calculate average age from date of birth
      let totalAge = 0;
      let ageCount = 0;
      const positionCounts = {};

      allTrialists.forEach((trialist) => {
        // Calculate age from dob
        if (trialist.dob) {
          const birthDate = new Date(trialist.dob);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          // Adjust age if birthday hasn't occurred this year
          const adjustedAge =
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
              ? age - 1
              : age;

          if (adjustedAge > 0 && adjustedAge < 50) {
            // Reasonable age range
            totalAge += adjustedAge;
            ageCount++;
          }
        }

        // Count positions
        if (trialist.position) {
          const position = trialist.position.trim();
          positionCounts[position] = (positionCounts[position] || 0) + 1;
        }
      });

      // Set average age
      this.averagePlayerAge =
        ageCount > 0 ? (totalAge / ageCount).toFixed(1) : 0;

      // Find top position
      let topPosition = "N/A";
      let maxCount = 0;
      Object.keys(positionCounts).forEach((position) => {
        if (positionCounts[position] > maxCount) {
          maxCount = positionCounts[position];
          topPosition = position;
        }
      });
      this.topPosition = topPosition;
    },

    // Calculate regional distribution data
    calculateRegionalData() {
      if (!this.allScoutsData.length && !this.allAssessmentsData.length) {
        this.topRegion = "N/A";
        this.topRegions = [];
        return;
      }

      // Collect region data from scouts and assessments
      const regionCounts = {};

      // Count regions from scouts (postcodes/addresses)
      this.allScoutsData.forEach((scout) => {
        let region = null;

        if (scout.post_code) {
          // Extract region from postcode (first part before space)
          region = scout.post_code.split(" ")[0].trim();
        } else if (scout.address) {
          // Extract region from address (last part after comma)
          const addressParts = scout.address.split(",");
          region = addressParts[addressParts.length - 1].trim();
        }

        if (region && region.length > 0) {
          regionCounts[region] = (regionCounts[region] || 0) + 1;
        }
      });

      // Count regions from game attendance data (venue addresses)
      this.gameAttendanceData.forEach((game) => {
        if (game.venue_address) {
          const addressParts = game.venue_address.split(",");
          const region = addressParts[addressParts.length - 1].trim();
          if (region && region.length > 0) {
            regionCounts[region] = (regionCounts[region] || 0) + 1;
          }
        }
      });

      // If no regions found, add default
      if (Object.keys(regionCounts).length === 0) {
        regionCounts["Unknown"] = 1;
      }

      // Find top region
      let topRegion = "N/A";
      let maxCount = 0;
      Object.keys(regionCounts).forEach((region) => {
        if (regionCounts[region] > maxCount) {
          maxCount = regionCounts[region];
          topRegion = region;
        }
      });
      this.topRegion = topRegion;

      // Create top regions array (top 4)
      const sortedRegions = Object.keys(regionCounts)
        .map((region) => ({
          name: region,
          count: regionCounts[region],
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 4);

      // Calculate percentages relative to the highest count
      const maxRegionCount =
        sortedRegions.length > 0 ? sortedRegions[0].count : 1;
      this.topRegions = sortedRegions.map((region) => ({
        name: region.name,
        count: region.count,
        percentage: Math.round((region.count / maxRegionCount) * 100),
      }));
    },

    calculateScoutActivityMetrics() {
      // Calculate scout activity metrics from gameAttendanceData
      if (!this.gameAttendanceData || this.gameAttendanceData.length === 0) {
        this.totalGamesAttended = 0;
        this.scoutEfficiencyRate = 0;
        this.activeScoutsCount = 0;
        this.scoutCoverageRegions = 0;
        this.topScouts = [];
        return;
      }

      // Calculate total games attended
      this.totalGamesAttended = this.gameAttendanceData.length;

      // Calculate active scouts count
      const uniqueScouts = new Set();
      const scoutMetrics = {};
      let totalLeads = 0;
      this.gameAttendanceData.forEach((game) => {
        const scoutId = game.scoutId || game.scout_id;
        if (scoutId) {
          uniqueScouts.add(scoutId);

          if (!scoutMetrics[scoutId]) {
            scoutMetrics[scoutId] = {
              name: this.getScoutName(scoutId),
              gamesAttended: 0,
              playersAssessed: 0,
              leadsGenerated: 0,
            };
          }

          scoutMetrics[scoutId].gamesAttended++;

          // Count players assessed in this game
          if (game.playersGameData && Array.isArray(game.playersGameData)) {
            const playersCount = game.playersGameData.length;
            scoutMetrics[scoutId].playersAssessed += playersCount;

            // Estimate leads as players with rating >= 6 average
            const leads = game.playersGameData.filter((player) => {
              const avgRating =
                ((parseInt(player.attitude) || 0) +
                  (parseInt(player.skill) || 0) +
                  (parseInt(player.physique) || 0) +
                  (parseInt(player.mentality) || 0) +
                  (parseInt(player.leadership) || 0) +
                  (parseInt(player.professionalism) || 0)) /
                6;
              return avgRating >= 6;
            }).length;

            scoutMetrics[scoutId].leadsGenerated += leads;
            totalLeads += leads;
          }
        }
      });

      this.activeScoutsCount = uniqueScouts.size;

      // Calculate efficiency rate (leads per game)
      this.scoutEfficiencyRate =
        this.totalGamesAttended > 0
          ? Math.round((totalLeads / this.totalGamesAttended) * 100) / 10
          : 0;

      // Calculate coverage regions
      const regions = new Set();
      this.gameAttendanceData.forEach((game) => {
        if (game.venue || game.location) {
          // Extract region from venue/location or use first word
          const location = game.venue || game.location || "";
          const region = location.split(",")[0] || location.split(" ")[0];
          if (region) regions.add(region);
        }
      });
      this.scoutCoverageRegions = regions.size;

      // Calculate top scouts
      const sortedScouts = Object.values(scoutMetrics)
        .sort((a, b) => b.gamesAttended - a.gamesAttended)
        .slice(0, 4);

      const maxGames =
        sortedScouts.length > 0 ? sortedScouts[0].gamesAttended : 1;
      this.topScouts = sortedScouts.map((scout) => ({
        name: scout.name,
        gamesAttended: scout.gamesAttended,
        percentage: Math.round((scout.gamesAttended / maxGames) * 100),
      }));
    },

    getScoutName(scoutId) {
      // Try to find scout name from allScoutsData prop
      if (this.allScoutsData && Array.isArray(this.allScoutsData)) {
        const scout = this.allScoutsData.find((s) => s.id == scoutId);
        if (scout)
          return (
            scout.name ||
            scout.firstName + " " + scout.lastName ||
            "Unknown Scout"
          );
      }
      return `Scout ${scoutId}`;
    },

    // Bucket assessment ratings into 1–3, 4–5, 6–7, 8–10. Each assessment
    // has six per-category star ratings (attitude/skill/physique/mentality/
    // leadership/professionalism); we average them per assessment, then count
    // how many fall in each bucket.
    computeRatingBuckets() {
      const buckets = [0, 0, 0, 0]; // [1-3, 4-5, 6-7, 8-10]
      const cats = [
        "attitude",
        "skill",
        "physique",
        "mentality",
        "leadership",
        "professionalism",
      ];
      (this.allAssessmentsData || []).forEach((a) => {
        let sum = 0;
        let count = 0;
        cats.forEach((c) => {
          const v = parseFloat(a[c]);
          if (!isNaN(v) && v > 0) {
            sum += v;
            count++;
          }
        });
        if (count === 0) return;
        const avg = sum / count;
        if (avg < 4) buckets[0]++;
        else if (avg < 6) buckets[1]++;
        else if (avg < 8) buckets[2]++;
        else buckets[3]++;
      });
      return buckets;
    },

    async fetchAnalyticsSummary() {
      try {
        const clubId =
          (this.userData && this.userData.clubId) || undefined;
        const response = await Trialists.find({
          action: "getAnalyticsSummary",
          clubId,
        });
        const data = response && response.data;
        if (!data || !data.success) return;

        // Use backend KPIs if returned; client-side values stay otherwise.
        if (data.kpis) {
          this.totalAssessments = data.kpis.totalAssessments ?? this.totalAssessments;
          this.signedPlayers = data.kpis.signedPlayers ?? this.signedPlayers;
          this.rejectedPlayers = data.kpis.rejectedPlayers ?? this.rejectedPlayers;
          this.activeScouts = data.kpis.activeScouts ?? this.activeScouts;
          this.conversionRateOverall =
            data.kpis.conversionRate ?? this.conversionRateOverall;
          this.conversionRateOverallRejected =
            data.kpis.rejectionRate ?? this.conversionRateOverallRejected;
        }

        // Backend rating distribution overrides the client-side bucketing
        // for accuracy when the data set is large.
        if (
          Array.isArray(data.ratingDistribution) &&
          this.miniDistributionChart
        ) {
          this.miniDistributionChart.data.datasets[0].data =
            data.ratingDistribution;
          this.miniDistributionChart.update("none");
        }
      } catch (e) {
        console.warn(
          "AnalyticsDashboardOverview: backend summary failed, using client aggregation.",
          e,
        );
      }
    },

    refreshMiniDistributionChart() {
      if (!this.miniDistributionChart) return;
      this.miniDistributionChart.data.datasets[0].data = this.computeRatingBuckets();
      this.miniDistributionChart.update("none");
    },

    createMiniDistributionChart() {
      const ctx = this.$refs.miniDistributionChart?.getContext("2d");
      if (!ctx) return;

      this.miniDistributionChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: ["1-3", "4-5", "6-7", "8-10"],
          datasets: [
            {
              data: this.computeRatingBuckets(),
              backgroundColor: [
                "rgba(220, 53, 69, 0.6)",
                "rgba(255, 193, 7, 0.6)",
                "rgba(40, 167, 69, 0.6)",
                "rgba(23, 162, 184, 0.6)",
              ],
              borderWidth: 0,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
          },
          scales: {
            x: { display: false },
            y: { display: false },
          },
          elements: {
            bar: { borderRadius: 4 },
          },
        },
      });
    },

    navigateToPerformanceBenchmarking() {
      this.$emit("navigate", "performance-benchmarking");
    },

    navigateToPlayerDemographics() {
      this.$emit("navigate", "player-demographics");
    },

    navigateToScoutActivity() {
      this.$emit("navigate", "scout-activity");
    },
  },
};
</script>

<style scoped>
.analytics-dashboard-overview {
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.section-header {
  text-align: center;
  margin-bottom: 2rem;
}

.section-header h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.section-header p {
  color: #6c757d;
  margin: 0;
}

/* KPI Cards */
.kpi-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.kpi-card {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 1rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.kpi-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.kpi-icon.primary {
  background: linear-gradient(135deg, #007bff, #6610f2);
}

.kpi-icon.success {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.kpi-icon.warning {
  background: linear-gradient(135deg, #ffc107, #fd7e14);
}

.kpi-icon.info {
  background: linear-gradient(135deg, #17a2b8, #6f42c1);
}

.kpi-content h4 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: #2c3e50;
}

.kpi-content p {
  margin: 0 0 0.5rem 0;
  color: #495057;
  font-weight: 500;
}

.kpi-trend {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  display: inline-block;
}

.kpi-trend.trend-positive {
  background: #d4edda;
  color: #155724;
}

.kpi-trend.trend-negative {
  background: #f8d7da;
  color: #721c24;
}

.kpi-trend.trend-neutral {
  background: #e2e3e5;
  color: #495057;
}

.kpi-detail {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

/* Dashboard Sections */
.dashboard-sections {
  display: grid;
  gap: 2rem;
  margin-bottom: 3rem;
}

.dashboard-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
  overflow: hidden;
}

.dashboard-section:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.section-header-card {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  border-bottom: 1px solid #f8f9fa;
}

.section-icon {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  flex-shrink: 0;
}

.section-icon.performance {
  background: linear-gradient(135deg, #007bff, #0056b3);
}

.section-icon.demographics {
  background: linear-gradient(135deg, #28a745, #1e7e34);
}

.section-icon.scout-activity {
  background: linear-gradient(135deg, #6f42c1, #4e2d8e);
}

.section-info {
  flex: 1;
}

.section-info h4 {
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.section-info p {
  color: #6c757d;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.section-metrics {
  display: flex;
  gap: 1rem;
}

.metric {
  background: #f8f9fa;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.section-action {
  font-size: 1.5rem;
  color: #007bff;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.dashboard-section:hover .section-action {
  opacity: 1;
}

/* Preview Charts */
.preview-charts {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 1.5rem 2rem;
  background: #f8f9fa;
}

.mini-chart h5,
.demo-chart-mini h5 {
  color: #495057;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.mini-chart canvas {
  max-height: 120px;
}

.mini-stats,
.demo-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #dee2e6;
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.stat-value {
  font-weight: 600;
  color: #2c3e50;
}

.stat-value.trend-positive {
  color: #28a745;
}

/* Demographics Preview */
.preview-demographics {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 1.5rem 2rem;
  background: #f8f9fa;
}

/* Scout Activity Preview */
.preview-scout-activity {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  padding: 1.5rem 2rem;
  background: #f8f9fa;
}

.scout-chart-mini h5 {
  color: #495057;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.scout-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.scout-mini {
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.scout-name {
  font-weight: 500;
  color: #495057;
}

.scout-bar-mini {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.scout-fill-mini {
  height: 100%;
  background: linear-gradient(90deg, #6f42c1, #4e2d8e);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.scout-count {
  font-weight: 600;
  color: #2c3e50;
  text-align: right;
}

.scout-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.region-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.region-mini {
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.region-name {
  font-weight: 500;
  color: #495057;
}

.region-bar-mini {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.region-fill-mini {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.region-count {
  font-weight: 600;
  color: #2c3e50;
  text-align: right;
}

/* Quick Actions */
.quick-actions {
  margin-top: 2rem;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.quick-actions h4 {
  color: #2c3e50;
  margin: 0 0 1.5rem 0;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.primary {
  background: #007bff;
  color: white;
}

.action-btn.primary:hover {
  background: #0056b3;
}

.action-btn.secondary {
  background: #6c757d;
  color: white;
}

.action-btn.secondary:hover {
  background: #545b62;
}

.action-btn.tertiary {
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
}

.action-btn.tertiary:hover {
  background: #e9ecef;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .analytics-dashboard-overview {
    padding: 1rem;
  }

  .kpi-cards {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .section-header-card {
    padding: 1.5rem;
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }

  .section-metrics {
    justify-content: center;
  }

  .preview-charts,
  .preview-demographics,
  .preview-scout-activity {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
